import { useState, useRef, useCallback } from "react";
import { Upload, X, FileText, AlertCircle, CheckCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Modal } from "@/shared/components/ui/Modal";
import { Button } from "@/shared/components/ui/Button";
import { validateFile } from "@/features/files/utils/sharepointValidation";
import { uploadFile } from "@/features/files/services/sharepointService";
import { formatFileSize } from "@/shared/utils/fileHelpers";

interface FileUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  folderId: string;
  onUploaded: () => void;
}

export const FileUploadModal = ({
  isOpen,
  onClose,
  folderId,
  onUploaded,
}: FileUploadModalProps) => {
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [validationError, setValidationError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [success, setSuccess] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setSelectedFile(null);
    setValidationError("");
    setUploading(false);
    setProgress(0);
    setSuccess(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFileSelect = useCallback((file: File) => {
    setSuccess(false);
    const result = validateFile(file);
    if (!result.valid) {
      setValidationError(result.error!);
      setSelectedFile(null);
      return;
    }
    setValidationError("");
    setSelectedFile(file);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFileSelect(file);
    },
    [handleFileSelect],
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
    // Reset input so the same file can be re-selected
    e.target.value = "";
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setProgress(0);
    try {
      await uploadFile(selectedFile, folderId, setProgress);
      setSuccess(true);
      setUploading(false);
      onUploaded();
      // Auto-close after brief delay
      setTimeout(handleClose, 800);
    } catch {
      setValidationError(t("sharepoint.upload_failed"));
      setUploading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={t("sharepoint.upload_file")}>
      <div className="space-y-4">
        {/* Drop zone */}
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
            dragOver
              ? "border-indigo-400 bg-indigo-50"
              : "border-gray-300 hover:border-gray-400 hover:bg-gray-50"
          }`}
        >
          <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-600">
            {t("sharepoint.drag_drop")} <span className="text-indigo-600 font-medium">{t("sharepoint.browse")}</span>
          </p>
          <p className="text-xs text-gray-400 mt-1">{t("sharepoint.file_limit_hint")}</p>
        </div>

        <input
          ref={inputRef}
          type="file"
          className="hidden"
          onChange={handleInputChange}
          accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.bmp,.svg,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.mp4,.mov,.m4v,.webm"
        />

        {/* Selected file info */}
        {selectedFile && !success && (
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <FileText className="w-5 h-5 text-gray-500 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900 truncate">{selectedFile.name}</p>
              <p className="text-xs text-gray-500">{formatFileSize(selectedFile.size)}</p>
            </div>
            {!uploading && (
              <button onClick={reset} className="p-1 hover:bg-gray-200 rounded">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            )}
          </div>
        )}

        {/* Progress bar */}
        {uploading && (
          <div className="space-y-1">
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-indigo-600 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-xs text-gray-500 text-right">{progress}%</p>
          </div>
        )}

        {/* Success */}
        {success && (
          <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <p className="text-sm text-green-700">{t("sharepoint.upload_success")}</p>
          </div>
        )}

        {/* Validation error */}
        {validationError && (
          <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg">
            <AlertCircle className="w-5 h-5 text-red-500 shrink-0" />
            <p className="text-sm text-red-600">{validationError}</p>
          </div>
        )}

        {/* Actions */}
        {!success && (
          <div className="flex gap-3 pt-2">
            <Button variant="secondary" onClick={handleClose} className="flex-1">
              {t("sharepoint.cancel")}
            </Button>
            <Button
              onClick={handleUpload}
              loading={uploading}
              disabled={!selectedFile || uploading}
              className="flex-1"
            >
              {t("sharepoint.upload")}
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
};
